import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navigation from "../../common/components/Navigation/Navigation";
import StepperMUI from "../../common/components/Stepper/Stepper";
import ProjectNewName from "./ProjectNewName";
import ProjectThumbnail from "./ProjectThumbnail";
import ProjectOverview from "./ProjectOverview";
import {
  getNewProjectSelector,
  projectsSelector,
} from "../../features/projects/state/projectsSlice";
import { useAppSelector } from "src/common/state/hooks";
import type { IProject } from "../../model/model";
import { getSelectedProjectGroup } from "src/features/projectGroups/state/projectGroupsSlice";
import ProjectLanguageSelectorWrapper from "./LanguageSelection/ProjectLanguageSelectorWrapper";
import { useTourView } from "../../features/onboarding/engine/useTourView";
import { hostedTourAnchors } from "../../features/onboarding/engine/tourViewAnchors";

const MODULE_WIZARD_TOUR_ANCHORS: Partial<Record<number, string>> = {
  0: "module-wizard-overview",
  2: "module-wizard-languages",
};
const CUSTOM_TEMPLATE_WIZARD_TOUR_ANCHORS: Partial<Record<number, string>> = {
  1: "module-wizard-languages",
};

export default function CreateProjectWrapper() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const incrementStep = () => setStep(step + 1);
  const decrementStep = () => setStep(step - 1);

  // If the template is a user-made custom template, only have 2 steps instead of 3
  const projects = useAppSelector(projectsSelector);
  const newProject = useAppSelector(getNewProjectSelector);
  const selectedProjectGroup = useAppSelector(getSelectedProjectGroup);

  const clonedProject = projects.find(
    (p: IProject) => Number(p.id) === Number(newProject.projectId),
  );
  const isCustomTemplate = clonedProject?.is_user_template;
  const stepperTourAnchors = isCustomTemplate
    ? CUSTOM_TEMPLATE_WIZARD_TOUR_ANCHORS
    : MODULE_WIZARD_TOUR_ANCHORS;
  const activeStepAnchor = stepperTourAnchors[step];
  useTourView(
    "module-wizard-stepper",
    true,
    hostedTourAnchors("module-wizard-stepper", activeStepAnchor ? [activeStepAnchor] : []),
  );

  const cancelCb = () => {
    navigate(`/courses/${selectedProjectGroup.id}/list`);
  };

  const renderStep = (step: number) => {
    if (isCustomTemplate) {
      switch (step) {
        case 0:
        default:
          return (
            <ProjectNewName
              incrementStep={incrementStep}
              decrementStep={decrementStep}
              onCancelClick={cancelCb}
            />
          );
        case 1:
          return (
            <ProjectLanguageSelectorWrapper
              incrementStep={incrementStep}
              decrementStep={decrementStep}
              mode="newProjectFromCustomTemplate"
            />
          );
        case 2:
          return (
            <ProjectThumbnail
              decrementStep={decrementStep}
              mode="createProject"
              onCancelClick={cancelCb}
            />
          );
      }
    } else {
      switch (step) {
        case 0:
        default:
          return <ProjectOverview incrementStep={incrementStep} />;
        case 1:
          return (
            <ProjectNewName
              incrementStep={incrementStep}
              decrementStep={decrementStep}
              onCancelClick={cancelCb}
            />
          );
        case 2:
          return (
            <ProjectLanguageSelectorWrapper
              incrementStep={incrementStep}
              decrementStep={decrementStep}
              mode="newProject"
            />
          );
        case 3:
          return (
            <ProjectThumbnail
              decrementStep={decrementStep}
              mode="createProject"
              onCancelClick={cancelCb}
            />
          );
      }
    }
  };

  return (
    <>
      <Navigation
        title={t("pages.createProject.title")}
        showGoBackButton
        backCustomRedirectTo={
          selectedProjectGroup?.id ? `/courses/${selectedProjectGroup.id}/list` : "/courses"
        }
      />
      <StepperMUI
        step={step}
        mode={isCustomTemplate ? "newProjectFromCustomTemplate" : "newProject"}
        tourAnchors={stepperTourAnchors}
      />
      {renderStep(step)}
    </>
  );
}
